// =====================================================================
// Assessment Center — Instructor Panel
// Dashboard page — at-a-glance counts and quick links only.
// Deliberately NOT analytics: no charts, trends, or score breakdowns.
// =====================================================================
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, Card, PageHeader, Spinner, statusTone } from "../../../shared/components/ui";
import { listExams, listQuestions, type ExamWithDetail, type QuestionWithDetail } from "../lib/mock-api";

export default function DashboardPage() {
  const [exams, setExams] = useState<ExamWithDetail[]>([]);
  const [questions, setQuestions] = useState<QuestionWithDetail[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([listExams(), listQuestions()]).then(([e, q]) => {
      setExams(e);
      setQuestions(q);
      setLoading(false);
    });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-20 text-slate-500">
        <Spinner /> Loading dashboard…
      </div>
    );
  }

  const draftExams = exams.filter((e) => e.status === "draft");
  const publishedExams = exams.filter((e) => e.status === "published" || e.status === "active");
  const draftQuestions = questions.filter((q) => q.status === "draft");
  const recentExams = exams.slice(0, 5);

  const stats = [
    { label: "Total exams", value: exams.length },
    { label: "Published / active", value: publishedExams.length },
    { label: "Draft exams", value: draftExams.length },
    { label: "Questions in bank", value: questions.length },
  ];

  return (
    <div>
      <PageHeader
        title="Dashboard"
        subtitle="Your Assessment Center at a glance."
        action={
          <div className="flex gap-2">
            <Link to="/instructor/assessment-center/questions/new">
              <Button variant="secondary">+ Create Question</Button>
            </Link>
            <Link to="/instructor/assessment-center/exams/new">
              <Button>+ Create Exam</Button>
            </Link>
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((s) => (
          <Card key={s.label} className="p-5">
            <p className="font-display text-3xl font-semibold text-navy-900">{s.value}</p>
            <p className="mt-1 text-sm text-slate-500">{s.label}</p>
          </Card>
        ))}
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between border-b border-border-soft px-5 py-4">
            <h3 className="font-display text-lg font-semibold text-navy-900">Recent exams</h3>
            <Link to="/instructor/assessment-center/exams" className="text-sm text-navy-700 hover:underline">
              View all
            </Link>
          </div>
          {recentExams.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-slate-500">
              No exams yet — create your first one.
            </p>
          ) : (
            <ul className="divide-y divide-border-soft">
              {recentExams.map((exam) => (
                <li key={exam.id} className="flex items-center justify-between px-5 py-3">
                  <div>
                    <Link
                      to={`/instructor/assessment-center/exams/${exam.id}/edit`}
                      className="text-sm font-medium text-navy-900 hover:underline"
                    >
                      {exam.title}
                    </Link>
                    <p className="text-xs text-slate-400">
                      {exam.sections.length} section{exam.sections.length === 1 ? "" : "s"} ·{" "}
                      {exam.duration_minutes} min
                    </p>
                  </div>
                  <Badge tone={statusTone(exam.status)}>{exam.status}</Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <div className="border-b border-border-soft px-5 py-4">
            <h3 className="font-display text-lg font-semibold text-navy-900">Needs attention</h3>
          </div>
          <ul className="divide-y divide-border-soft text-sm">
            <li className="flex items-center justify-between px-5 py-3">
              <span className="text-slate-600">Draft exams</span>
              <span className="font-medium text-navy-900">{draftExams.length}</span>
            </li>
            <li className="flex items-center justify-between px-5 py-3">
              <span className="text-slate-600">Unpublished questions</span>
              <span className="font-medium text-navy-900">{draftQuestions.length}</span>
            </li>
          </ul>
          <div className="px-5 pb-5">
            <Link to="/instructor/assessment-center/question-bank">
              <Button variant="secondary" className="w-full">
                Review Question Bank
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
